import { useLazyQuery } from "@apollo/client";
import { clientNeoAuth } from "@neonumy/api-clients";
import * as Sentry from "@sentry/browser";
import { GET_USER_ACCESS_QUERY } from "queries/neoauth/users";
import { useEffect, useRef, useState } from "react";
import { onErrorGraphql } from "utils/onErrorGraphql";

// Lowercase and without accents, so "evora" finds "Évora".
const toSearchText = (text: string) =>
  text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLocaleLowerCase();

export function useUserAccess({ userId }: { userId: ID }) {
  const accountingFirmsId = useRef<ID[] | undefined>(undefined);
  const [filters, setFilters] = useState<{ accountingFirm?: string }>({});

  const [
    listAccountingFirmsAccess,
    { data: userAccessData, error: userAccessError, refetch, loading },
  ] = useLazyQuery<ListAccountingFirmsAccessOutput, ListAccountingFirmsAccessInput>(
    GET_USER_ACCESS_QUERY,
    { client: clientNeoAuth },
  );

  useEffect(() => {
    if (userAccessData) {
      const { getUserAccesses } = userAccessData;
      if (
        getUserAccesses?.accountingFirms?.length === undefined ||
        getUserAccesses?.accountingFirms?.length === 0
      ) {
        Sentry.captureException(new Error("Invalid accounting firm access"), {
          extra: {
            response: getUserAccesses,
            accountingFirm: getUserAccesses?.accountingFirms,
          },
        });
      }
      accountingFirmsId.current = getUserAccesses.accountingFirms.map(item => item.id);
    }
  }, [userAccessData]);

  useEffect(() => {
    if (userAccessError) onErrorGraphql(userAccessError);
  }, [userAccessError]);

  useEffect(() => {
    if (userId) {
      listAccountingFirmsAccess({ variables: { id: userId } });
    }
  }, [userId]);

  const accountingFirms = userAccessData?.getUserAccesses?.accountingFirms ?? [];

  // The query returns every firm, so the search filters on the client.
  const search = filters.accountingFirm && toSearchText(filters.accountingFirm);
  const filteredAccountingFirms = search
    ? accountingFirms.filter(item => toSearchText(item.name ?? "").includes(search))
    : accountingFirms;

  const dataSource = filteredAccountingFirms.map(item => ({
    key: item.id,
    id: item.id,
    accountingFirm: item.name,
  }));

  const handleSearchFilters = (confirm: () => void) => confirm();

  return {
    dataSource,
    refetch,
    loading,
    accountingFirmsId,
    filters,
    setFilters,
    handleSearchFilters,
  };
}
